import { useTranslation } from "react-i18next";
import CRUDTable from "../CRUDTable";
import React from "react";
import { Chip } from "@mui/material";
import { getFilials } from "../../../redux/actions/filialActions";
import { getDepartments } from "../../../redux/actions/departmentActions";
import { deviceIndicatorCRUD, filialDivisionCRUD } from "../../../http";
import { exportDeviceIndicatorExcel } from "../../../screens/EquipmentIndicator/deviceIndicatorExcel";

// Функция для расчета статуса на основе даты следующей поверки
const calculateStatus = (nextVerificationDate) => {
    if (!nextVerificationDate) {
        return { status: "Не задано", color: "default" };
    }

    const parseDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value === "string") {
            // DD.MM.YYYY
            if (value.includes(".")) {
                const parts = value.split(".");
                if (parts.length !== 3) return null;
                return new Date(
                    parseInt(parts[2], 10),
                    parseInt(parts[1], 10) - 1,
                    parseInt(parts[0], 10)
                );
            }
            // ISO / any Date-parsable string
            const d = new Date(value);
            return isNaN(d.getTime()) ? null : d;
        }
        return null;
    };

    const nextDate = parseDate(nextVerificationDate);
    if (!nextDate || isNaN(nextDate.getTime())) {
        return { status: "Неверная дата", color: "default" };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    nextDate.setHours(0, 0, 0, 0);

    // Вычисляем разницу в днях
    const diffTime = nextDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Если дата уже прошла
    if (diffDays < 0) {
        return { status: "Просрочено", color: "error" };
    }

    // Вычисляем разницу в месяцах (более точно)
    // Используем количество дней для более точного расчета
    const monthsDiff = diffDays / 30.44; // Среднее количество дней в месяце

    // За 3 месяца до даты (>= 3 месяцев) - зеленый (Норма)
    if (monthsDiff >= 3) {
        return { status: "Норма", color: "success" };
    }
    // За 2 месяца до даты (>= 2 и < 3 месяцев) - желтый (Внимание)
    else if (monthsDiff >= 2) {
        return { status: "Внимание", color: "warning" };
    }
    // За 1 месяц до даты (>= 1 и < 2 месяцев) - красный (Срочно)
    else if (monthsDiff >= 1) {
        return { status: "Срочно", color: "error" };
    }
    // Меньше 1 месяца до даты - красный (Срочно)
    else if (monthsDiff >= 0) {
        return { status: "Срочно", color: "error" };
    }
    // Дата уже прошла
    else {
        return { status: "Просрочено", color: "error" };
    }
};

const formatDate = (value) => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("ru-RU");
};

export default function EquipmentIndicatorCRUDTable({ extraQuery: extraQueryProp, ...props }) {
    const { t } = useTranslation();

    const columns = [
        { title: "Id", dataIndex: "id", key: "id" },
        {
            title: "Филиал",
            dataIndex: "filial",
            key: "filial",
            render: (value) => value?.name || "",
        },
        {
            title: "Районное отделение",
            dataIndex: "filialDivision",
            key: "filialDivision",
            render: (value) => value?.name || "",
        },
        {
            title: "Лаборатория",
            dataIndex: "department",
            key: "department",
            render: (value) => value?.name || "",
        },
        {
            title: "Наименование",
            dataIndex: "name",
            key: "name",
        },
        { title: "Тип", dataIndex: "type", key: "type" },
        { title: "Заводской номер", dataIndex: "serialNumber", key: "serialNumber" },
        { title: "Год выпуска", dataIndex: "manufactureYear", key: "manufactureYear" },
        { title: "Количество", dataIndex: "quantity", key: "quantity" },
        {
            title: "Разряд/класс/погрешность",
            dataIndex: "rankAccuracyError",
            key: "rankAccuracyError",
        },
        { title: "Диапазон измерения", dataIndex: "measurementRange", key: "measurementRange" },
        {
            title: "Дата последней поверки",
            dataIndex: "lastVerificationDate",
            key: "lastVerificationDate",
            render: (value) => formatDate(value),
        },
        {
            title: "Дата последующей поверки",
            dataIndex: "nextVerificationDate",
            key: "nextVerificationDate",
            render: (value) => formatDate(value),
        },
        { title: "Примечание", dataIndex: "note", key: "note" },
        { title: "Периодичность", dataIndex: "periodicity", key: "periodicity" },
        {
            title: "Статус",
            dataIndex: "nextVerificationDate",
            key: "status",
            render: (_, record) => {
                const statusInfo = calculateStatus(record.nextVerificationDate);
                return <Chip label={statusInfo.status} color={statusInfo.color} size="small" />;
            },
        },
    ];

    const filterFields = [
        {
            title: "Филиал",
            key: "filialId",
            compareType: "equals",
            reducerKey: "filial",
            reducerArrayKey: "filials",
            reducerAction: getFilials,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Районное отделение",
            key: "filialDivisionId",
            compareType: "equals",
            reducerKey: "filialDivision",
            operandPosition: 1,
            parentKey: "filialId",
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
            useDomainSelect2: true,
            domainSelectCrud: filialDivisionCRUD,
            domainSelect2SkipFetchWithoutParent: true,
            domainSelect2NoParentHint: "Выберите филиал",
            selectMembers: ["Id", "Name", "FilialId"],
            normalizeOption: (row) => ({
                ...row,
                id: row.id ?? row.Id,
                name: row.name ?? row.Name,
                filialId: row.filialId ?? row.FilialId,
            }),
        },
        {
            title: "Лаборатория",
            key: "departmentId",
            compareType: "equals",
            reducerKey: "department",
            reducerArrayKey: "departments",
            reducerAction: getDepartments,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Дата последней поверки",
            key: "lastVerificationDate",
            compareType: "equals",
            operandPosition: 1,
            type: "date",
            filterType: "normal",
        },
        {
            title: "Дата последующей поверки",
            key: "nextVerificationDate",
            compareType: "equals",
            operandPosition: 1,
            type: "date",
            filterType: "normal",
        },
        {
            title: "Неактуальный",
            key: "isIrrelevant",
            compareType: "equals",
            operandPosition: 1,
            type: "normal",
            filterType: "normal",
            marginTop: 8,
            options: [
                { id: false, name: "Нет" },
                { id: true, name: "Да" },
            ],
        },
    ];

    // Функция для определения класса строки на основе статуса
    const rowClassNameHandler = (record) => {
        const statusInfo = calculateStatus(record.nextVerificationDate);
        
        // Возвращаем классы для окраски строк
        switch (statusInfo.status) {
            case "Норма":
                return "equipment-indicator-normal"; // Зеленый фон
            case "Внимание":
                return "equipment-indicator-warning"; // Желтый фон
            case "Срочно":
                return "equipment-indicator-urgent"; // Красный фон
            case "Просрочено":
                return "equipment-indicator-overdue"; // Красный фон
            default:
                return "";
        }
    };

    const mergedExtraQuery = {
        ...(extraQueryProp || {}),
        includes: [
            ...new Set([
                "FilialDivision",
                ...((extraQueryProp && extraQueryProp.includes) || []),
            ]),
        ],
    };

    return (
        <CRUDTable
            search={deviceIndicatorCRUD.search}
            extraQuery={mergedExtraQuery}
            columns={columns}
            filterFields={filterFields}
            rowClassNameHandler={rowClassNameHandler}
            onExportExcel={async ({ filter, sort, fileName }) => {
                const data = await deviceIndicatorCRUD.search({
                    paging: { skip: 0, take: null, returnCount: true },
                    filter,
                    sort,
                });
                exportDeviceIndicatorExcel(data?.result || [], { fileName });
            }}
            exportExcelFileName={"Индикатор_поверки_оборудования.xlsx"}
            {...props}
        />
    );
}

